import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { importControlAPI } from '../services/importControlAPI';

const AutomatedImports = ({ toastRef }) => {
  const [searchParams] = useSearchParams();
  const [pendings, setPendings] = useState([]);
  const [currentPending, setCurrentPending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [completedUploads, setCompletedUploads] = useState([]);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    const validateAndLoad = async () => {
      const company = searchParams.get('company');
      const pendingsParam = searchParams.get('pendings');
      const base = searchParams.get('base');
      const token = searchParams.get('token');

      if (!company || !pendingsParam || !base || !token) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Erro',
          detail: 'Parâmetros inválidos no link'
        });
        setTokenValid(false);
        return;
      }

      try {
        // Validar token com backend
        const validationResponse = await importControlAPI.validateToken({
          token,
          enterprise_id: company,
          pendings: pendingsParam
        });

        if (!validationResponse.success) {
          throw new Error('Token inválido');
        }

        // Decodificar pendings
        const decodedPendings = JSON.parse(atob(pendingsParam));
        setPendings(decodedPendings);
        setTokenValid(true);

      } catch (error) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Erro',
          detail: 'Token inválido ou expirado'
        });
        setTokenValid(false);
      }
    };

    validateAndLoad();
  }, [searchParams, toastRef]);

  const handleUpload = async () => {
    if (!currentPending || !selectedFile) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione um arquivo para upload'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('enterprise_id', currentPending.layout_id);
      formData.append('enterprise_name', 'Empresa'); // TODO: obter do contexto
      formData.append('layout_name', currentPending.layout_name);
      formData.append('month', currentPending.month);

      // Simulação de progresso
      const interval = setInterval(() => {
        setUploadProgress(prev => (prev >= 90 ? prev : prev + 10));
      }, 150);

      await importControlAPI.upload(formData);

      clearInterval(interval);
      setUploadProgress(100);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: `Arquivo para ${currentPending.layout_name} - ${currentPending.month} enviado com sucesso!`
      });

      // Marcar como concluído
      setCompletedUploads(prev => [...prev, currentPending.import_id]);

      setTimeout(() => {
        setCurrentPending(null);
        setSelectedFile(null);
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.message || 'Falha ao enviar arquivo'
      });
    } finally {
      setUploading(false);
    }
  };

  const pendingItems = pendings.filter(p => !completedUploads.includes(p.import_id));

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <i className="pi pi-exclamation-triangle text-red-500 text-4xl mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Link Inválido ou Expirado
            </h3>
            <p className="text-gray-600">
              Este link não é válido ou já expirou.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <i className="pi pi-spinner pi-spin text-blue-500 text-4xl mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Validando Link...
            </h3>
            <p className="text-gray-600">
              Aguarde enquanto validamos seu link de acesso.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <Toast ref={toastRef} />

      <div className="max-w-4xl mx-auto">
        <Card className="mb-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Importações Automatizadas
            </h1>
            <p className="text-gray-600">
              Faça upload dos arquivos pendentes solicitados por email
            </p>
          </div>
        </Card>

        {pendingItems.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <i className="pi pi-check-circle text-green-500 text-4xl mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Todos os uploads concluídos!
              </h3>
              <p className="text-gray-600">
                Você receberá um email de confirmação em breve.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4">
            {pendingItems.map((pending, index) => (
              <Card key={pending.import_id} className="cursor-pointer hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge value={index + 1} severity="info" />
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {pending.layout_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Competência: {pending.month}
                      </p>
                    </div>
                  </div>
                  <Button
                    label="Fazer Upload"
                    icon="pi pi-upload"
                    onClick={() => setCurrentPending(pending)}
                    className="p-button-primary"
                  />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Upload */}
      <Dialog
        visible={!!currentPending}
        onHide={() => !uploading && setCurrentPending(null)}
        header="Upload de Arquivo"
        modal
        style={{ width: '500px' }}
        closable={!uploading}
      >
        {currentPending && (
          <div className="flex flex-column gap-4 mt-2">
            <div className="text-center">
              <h3 className="font-semibold text-lg">
                {currentPending.layout_name}
              </h3>
              <p className="text-sm text-gray-600">
                Competência: {currentPending.month}
              </p>
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-semibold">Arquivo *</label>
              <div
                className="border-2 border-dashed border-round p-4 text-center surface-card"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) setSelectedFile(file);
                }}
              >
                <FileUpload
                  mode="basic"
                  chooseLabel="Selecionar Arquivo"
                  className="w-full"
                  auto
                  customUpload
                  uploadHandler={(e) => setSelectedFile(e.files[0])}
                  accept=".pdf,.xlsx,.xls,.csv"
                />
                {selectedFile && (
                  <div className="mt-3 p-2 surface-100 border-round flex align-items-center justify-content-between">
                    <div className="flex align-items-center">
                      <i className="pi pi-file mr-2 text-primary"></i>
                      <span className="font-medium truncate" style={{ maxWidth: '250px' }}>
                        {selectedFile.name}
                      </span>
                    </div>
                    <Button
                      icon="pi pi-trash"
                      className="p-button-danger p-button-text p-button-rounded"
                      onClick={() => setSelectedFile(null)}
                    />
                  </div>
                )}
              </div>
            </div>

            {uploading && (
              <div className="flex flex-column gap-2">
                <span className="text-sm">Processando...</span>
                <ProgressBar value={uploadProgress} showValue={false} className="h-2" />
              </div>
            )}

            <div className="flex gap-2 justify-content-end">
              <Button
                label="Cancelar"
                icon="pi pi-times"
                onClick={() => setCurrentPending(null)}
                className="p-button-text"
                disabled={uploading}
              />
              <Button
                label="Importar"
                icon="pi pi-upload"
                onClick={handleUpload}
                loading={uploading}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AutomatedImports;